export interface CloudSyncResult {
  success: boolean;
  playerId?: string;
  updatedAt?: number;
  error?: string;
}

export interface CloudSaveData {
  saveData: string;
  playtimeSec: number;
  slimeMassKg: number;
  updatedAt: number;
}

export interface CloudSaveSyncOptions {
  apiUrl?: string;
  playerId?: string;
  syncIntervalSec?: number;
  debounceCooldownSec?: number;
  fetchFn?: typeof fetch;
  storage?: Storage;
  onSyncSuccess?: (info: { playerId: string; timestamp: number }) => void;
  onSyncError?: (error: string) => void;
}

export class CloudSaveSync {
  private apiUrl: string;
  private playerId: string;
  private syncIntervalSec: number;
  private debounceCooldownSec: number;
  private fetchFn: typeof fetch;
  private storage: Storage | null;
  private onSyncSuccess?: (info: { playerId: string; timestamp: number }) => void;
  private onSyncError?: (error: string) => void;

  private elapsedSinceLastSync = 0;
  private lastManualSyncTime = 0;
  private isSyncing = false;

  constructor(options: CloudSaveSyncOptions = {}) {
    this.apiUrl = options.apiUrl ?? (
      typeof window !== 'undefined' && window.location.hostname !== 'localhost'
        ? 'https://slimebound-api.e24qdy.workers.dev/api'
        : '/api'
    );
    this.syncIntervalSec = options.syncIntervalSec ?? 300.0;
    this.debounceCooldownSec = options.debounceCooldownSec ?? 15.0;
    this.fetchFn = options.fetchFn ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : () => Promise.reject(new Error('No fetch available')));
    this.storage = options.storage ?? (typeof localStorage !== 'undefined' ? localStorage : null);
    this.onSyncSuccess = options.onSyncSuccess;
    this.onSyncError = options.onSyncError;

    this.playerId = options.playerId || this.resolvePlayerId();
  }

  private resolvePlayerId(): string {
    const key = 'slimebound_player_id';
    const existing = this.storage?.getItem(key);
    if (existing) {
      return existing;
    }
    const generated = 'slime_' + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
    this.storage?.setItem(key, generated);
    return generated;
  }

  public getPlayerId(): string {
    return this.playerId;
  }

  public setPlayerId(id: string): void {
    this.playerId = id;
    this.storage?.setItem('slimebound_player_id', id);
  }

  public update(
    dt: number,
    getSavePayload: () => { saveData: string; playtimeSec: number; slimeMassKg: number } | null
  ): void {
    this.elapsedSinceLastSync += dt;
    if (this.elapsedSinceLastSync >= this.syncIntervalSec) {
      this.elapsedSinceLastSync = 0;
      const payload = getSavePayload();
      if (payload) {
        this.syncToCloud(payload.saveData, {
          playtimeSec: payload.playtimeSec,
          slimeMassKg: payload.slimeMassKg
        }).catch((err) => {
          this.onSyncError?.(err instanceof Error ? err.message : String(err));
        });
      }
    }
  }

  public async syncToCloud(
    saveDataJson: string,
    meta: { playtimeSec: number; slimeMassKg: number }
  ): Promise<CloudSyncResult> {
    const now = Date.now() / 1000;
    if (this.isSyncing) {
      return { success: false, error: 'Sync already in progress.' };
    }

    if (now - this.lastManualSyncTime < this.debounceCooldownSec) {
      return {
        success: false,
        error: `Cloud sync throttled. Please wait ${Math.ceil(this.debounceCooldownSec - (now - this.lastManualSyncTime))}s.`
      };
    }

    this.isSyncing = true;
    try {
      const response = await this.fetchFn(`${this.apiUrl}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Player-Id': this.playerId
        },
        body: JSON.stringify({
          playerId: this.playerId,
          saveData: saveDataJson,
          version: 1,
          playtimeSec: meta.playtimeSec,
          slimeMassKg: meta.slimeMassKg
        })
      });

      if (!response.ok) {
        const errorBody = (await response.json().catch(() => ({}))) as { error?: string };
        const msg = errorBody.error || `HTTP ${response.status} ${response.statusText}`;
        this.onSyncError?.(msg);
        return { success: false, error: msg };
      }

      const resData = (await response.json()) as { updatedAt: number; playerId: string };
      this.lastManualSyncTime = now;
      this.elapsedSinceLastSync = 0;
      this.onSyncSuccess?.({ playerId: this.playerId, timestamp: resData.updatedAt });

      return {
        success: true,
        playerId: this.playerId,
        updatedAt: resData.updatedAt
      };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.onSyncError?.(msg);
      return { success: false, error: msg };
    } finally {
      this.isSyncing = false;
    }
  }

  public async fetchFromCloud(): Promise<CloudSaveData | null> {
    try {
      const response = await this.fetchFn(`${this.apiUrl}/save/${this.playerId}`, {
        method: 'GET',
        headers: {
          'X-Player-Id': this.playerId
        }
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch cloud save (HTTP ${response.status})`);
      }

      const data = (await response.json()) as {
        saveData: string;
        playtimeSec: number;
        slimeMassKg: number;
        updatedAt: number;
      };

      return {
        saveData: data.saveData,
        playtimeSec: data.playtimeSec,
        slimeMassKg: data.slimeMassKg,
        updatedAt: data.updatedAt
      };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.onSyncError?.(msg);
      return null;
    }
  }
}
